/** @module Type-safe route tree (code-based TanStack Router) with a session guard. */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { AppShell } from "@/app/app-shell";
import { AdminPage } from "@/features/admin/admin-page";
import { CardPage } from "@/features/catalog/card-page";
import { SearchPage } from "@/features/catalog/search-page";
import { SetPage } from "@/features/catalog/set-page";
import { SetsPage } from "@/features/catalog/sets-page";
import { LoginPage } from "@/features/auth/login-page";
import { RegisterPage } from "@/features/auth/register-page";
import { MorePage } from "@/features/more/more-page";
import { getSession } from "@/lib/session";
import { CollectionPage } from "@/features/collection/collection-page";
import { fromSearch } from "@/features/collection/collection-filters";
import { ImportPage } from "@/features/collection/import-page";
import { PrintPage } from "@/features/collection/print-page";
import { DashboardPage } from "@/features/dashboard/dashboard-page";
import { LocationsPage } from "@/features/locations/locations-page";
import { ReviewPage } from "@/features/review/review-page";
import { ScanPage } from "@/features/scan/scan-page";
import { SettingsPage } from "@/features/settings/settings-page";
import { StatusPage } from "@/features/status/status-page";

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

/** Every route but the login needs a session; the guard runs before the component. */
function requireSession(): void {
  if (getSession().status !== "signed-in") {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- the router redirects by throwing
    throw redirect({ to: "/login" });
  }
}

/** The admin screen shows the whole machine; a plain account never gets there. */
function requireAdmin(): void {
  requireSession();
  if (getSession().account?.role !== "admin") {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- the router redirects by throwing
    throw redirect({ to: "/" });
  }
}

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: RegisterPage,
  validateSearch: (search: Record<string, unknown>): { invite?: string } =>
    typeof search.invite === "string" ? { invite: search.invite } : {},
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  beforeLoad: () => {
    if (getSession().status === "signed-in") {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- the router redirects by throwing
      throw redirect({ to: "/" });
    }
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardPage,
  beforeLoad: requireSession,
});

const collectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection",
  component: CollectionPage,
  beforeLoad: requireSession,
  validateSearch: (
    search: Record<string, unknown>,
  ): { status?: "archived"; purchase?: "with" | "without"; location?: string } => ({
    ...(search.status === "archived" ? { status: "archived" as const } : {}),
    ...(search.purchase === "with" || search.purchase === "without"
      ? { purchase: search.purchase }
      : {}),
    ...(typeof search.location === "string" && search.location
      ? { location: search.location }
      : {}),
  }),
});

const locationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/locations",
  component: LocationsPage,
  beforeLoad: requireSession,
});

const importRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection/import",
  component: ImportPage,
  beforeLoad: requireSession,
});

// The filters travel in the address, so the list prints what the collection showed.
const printRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection/print",
  component: PrintPage,
  beforeLoad: requireSession,
  validateSearch: fromSearch,
});

const moreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/more",
  component: MorePage,
  beforeLoad: requireSession,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  component: SearchPage,
  beforeLoad: requireSession,
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === "string" && search.q ? { q: search.q } : {},
});

const cardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cards/$printId",
  component: CardPage,
  beforeLoad: requireSession,
});

const setsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sets",
  component: SetsPage,
  beforeLoad: requireSession,
});

const setRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sets/$setId",
  component: SetPage,
  beforeLoad: requireSession,
});

const scanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scan",
  component: ScanPage,
  beforeLoad: requireSession,
});

const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/review",
  component: ReviewPage,
  beforeLoad: requireSession,
  validateSearch: (search: Record<string, unknown>): { scan?: string } =>
    typeof search.scan === "string" ? { scan: search.scan } : {},
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsPage,
  beforeLoad: requireSession,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
  beforeLoad: requireAdmin,
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === "string" ? { tab: search.tab } : {},
});

const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/status",
  component: StatusPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  dashboardRoute,
  collectionRoute,
  importRoute,
  printRoute,
  locationsRoute,
  moreRoute,
  searchRoute,
  cardRoute,
  setsRoute,
  setRoute,
  scanRoute,
  reviewRoute,
  settingsRoute,
  adminRoute,
  statusRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
